<template>
  <div class="inspection-item-title-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('inspectionItemTitleManage.page.title')" :subtitle="$t('inspectionItemTitleManage.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openAddModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.titleId" clearable class="w-full"
            :placeholder="$t('inspectionItemTitleManage.placeholderTitleId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.itemTitle" clearable class="w-full"
            :placeholder="$t('inspectionItemTitleManage.placeholderItemTitle')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.titleType" clearable class="w-full"
            :placeholder="$t('inspectionItemTitleManage.placeholderTitleType')">
            <el-option :label="$t('inspectionItemTitleManage.selectTitleType')" value="" />
            <el-option :label="$t('inspectionItemTitleManage.singleChoice')" value="1001" />
            <el-option :label="$t('inspectionItemTitleManage.multipleChoice')" value="2002" />
            <el-option :label="$t('inspectionItemTitleManage.shortAnswer')" value="3003" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="fetchList">
        <template #left>
          <span class="ds-panel__title">{{ $t('inspectionItemTitleManage.listTitle') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('inspectionItemTitleManage.page.empty')" @retry="fetchList">
        <el-table-column prop="titleId" :label="$t('inspectionItemTitleManage.titleId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.titleId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="itemTitle" :label="$t('inspectionItemTitleManage.title')" align="left" min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.itemTitle || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="titleType" :label="$t('inspectionItemTitleManage.titleType')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveTitleType(scope.row)" :label="getTitleTypeName(scope.row.titleType)" />
          </template>
        </el-table-column>
        <el-table-column prop="seq" :label="$t('inspectionItemTitleManage.seq')" align="center" width="90">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.seq }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('inspectionItemTitleManage.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="140" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="openEditModal(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteModal(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('inspectionItemTitleManage.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 组件 -->
    <add-inspection-item-title ref="addModal" @success="fetchList" />
    <edit-inspection-item-title ref="editModal" @success="fetchList" />
    <delete-inspection-item-title ref="deleteModal" @success="fetchList" />
  </div>
</template>

<script>
import { listInspectionItemTitle } from '@/api/inspection/inspectionItemTitleManageApi'
import AddInspectionItemTitle from '@/components/inspection/addInspectionItemTitle'
import EditInspectionItemTitle from '@/components/inspection/editInspectionItemTitle'
import DeleteInspectionItemTitle from '@/components/inspection/deleteInspectionItemTitle'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'InspectionItemTitleManageList',
  components: {
    AddInspectionItemTitle,
    EditInspectionItemTitle,
    DeleteInspectionItemTitle,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        titleId: '',
        itemTitle: '',
        titleType: '',
        itemId: this.$route.query.itemId || ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: getCommunityId()
        }

        const { data, total } = await listInspectionItemTitle(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取巡检题目失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('inspectionItemTitleManage.page.loadFailed')
        this.$message.error(this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchList()
    },
    handleReset() {
      this.searchForm = {
        titleId: '',
        itemTitle: '',
        titleType: '',
        itemId: this.searchForm.itemId
      }
      this.handleSearch()
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.current = 1
      this.fetchList()
    },
    handlePageChange(page) {
      this.pagination.current = page
      this.fetchList()
    },
    getTitleTypeName(type) {
      const types = {
        '1001': this.$t('inspectionItemTitleManage.singleChoice'),
        '2002': this.$t('inspectionItemTitleManage.multipleChoice'),
        '3003': this.$t('inspectionItemTitleManage.shortAnswer')
      }
      return types[type] || type
    },
    /**
     * 题目类型语义标签：
     * 1001 单选 / 2002 多选 / 3003 简答
     */
    resolveTitleType(row) {
      if (!row) return 'muted'
      if (row.titleType === '1001') return 'primary'
      if (row.titleType === '2002') return 'info'
      if (row.titleType === '3003') return 'muted'
      return 'muted'
    },
    goBack() {
      this.$router.go(-1)
    },
    openAddModal() {
      this.$refs.addModal.open({
        itemId: this.searchForm.itemId
      })
    },
    openEditModal(row) {
      this.$refs.editModal.open(row)
    },
    openDeleteModal(row) {
      this.$refs.deleteModal.open(row)
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.inspection-item-title-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
